import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from '@cian/newbuilding-utils';

import { manifestationTypeDescriptors } from '../../constants/descriptors';
import { isDescriptorActive, IDescriptorMappedForUI } from '../../utils/filters';
import { CheckboxButtonsMultiple } from '../../components/CheckboxButtonsMultiple';
import { TTypedThunkDispatch } from '../../types/thunk';
import { getOffersCount, getOffersData } from '../../actions/offersData';
import { updateTags } from '../../actions/filterTags';
import { IApplicationState } from '../../types/redux';
import { selectCanUseHiddenBase } from '../../selectors/user';
import { FILTER_SELECTION_TIMEOUT } from '../../constants/timeouts';

export const ManifestationType: React.FC = React.memo(() => {
  const dispatch = useDispatch<TTypedThunkDispatch>();
  const jsonQuery = useSelector((state: IApplicationState) => state.jsonQuery);
  const canUseHiddenBase = useSelector(selectCanUseHiddenBase);

  const applyFilter = React.useCallback(() => {
    dispatch(getOffersData({ canUseHiddenBase }));
  }, [dispatch, canUseHiddenBase]);

  const applyFilterDebounced = React.useRef(debounce(FILTER_SELECTION_TIMEOUT, applyFilter));

  React.useEffect(() => {
    applyFilterDebounced.current = debounce(FILTER_SELECTION_TIMEOUT, applyFilter);
  }, [applyFilter]);

  const stack = React.useMemo(
    () =>
      manifestationTypeDescriptors.reduce(
        (fits: IDescriptorMappedForUI[], { label, state }) =>
          state === '' ? fits : fits.concat({ value: state, text: label }),
        [],
      ),
    [],
  );

  const activeDescriptor = React.useMemo(
    () => manifestationTypeDescriptors.find(descriptor => isDescriptorActive(descriptor)(jsonQuery)),
    [jsonQuery],
  );

  const value = activeDescriptor ? activeDescriptor.state : undefined;

  const handleChange = React.useCallback(
    (newValue: string) => {
      const currentValue = activeDescriptor && activeDescriptor.state;

      let wanted = newValue;
      if (wanted === currentValue) {
        wanted = '';
      }

      const descriptor = manifestationTypeDescriptors.find(d => d.state === wanted);

      if (descriptor) {
        dispatch({
          type: `${descriptor.name}/${descriptor.state}`,
          value: wanted,
        });
        dispatch(updateTags());
        dispatch(getOffersCount());
      }
    },
    [dispatch, activeDescriptor],
  );

  return <CheckboxButtonsMultiple stack={stack} onChange={handleChange} value={value} />;
});

ManifestationType.displayName = 'ManifestationType';
